import arrowClockwiseIcon from "assets/svg/chatFiles/ArrowClockwise.svg";
import arrowFatDownIcon from "assets/svg/chatFiles/ArrowFatDown.svg";
import fileIcon from "assets/svg/chatFiles/File.svg";
import fileDeleteIcon from "assets/svg/chatFiles/FileDelete.svg";

import styles from "./index.module.css";
import { formatMegabytes, getFileExtension } from "./utils";

// Состояния компонента image-chat из Figma
export type FileTileState = "loading" | "loading-preview" | "loaded" | "unloaded-input-error" | "unloaded-error";

type Props = {
  kind: "img" | "file";
  state: FileTileState;
  name: string;
  previewUrl?: string;
  // Размер в МБ
  size?: number | null;
  percent?: number;
  onClick?: () => void;
  onDelete?: () => void;
  onImageLoad?: () => void;
  onImageError?: () => void;
};

const RING_RADIUS = 11;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;

const ProgressRing = ({ percent }: { percent?: number }) => {
  const isSpinner = percent === undefined;
  const progress = isSpinner ? 0.25 : Math.min(percent, 100) / 100;
  return (
    <svg className={`${styles.ring} ${isSpinner ? styles.spin : ""}`} viewBox="0 0 24 24">
      <circle className={styles.ringTrack} cx="12" cy="12" r={RING_RADIUS} />
      <circle
        className={styles.ringValue}
        cx="12"
        cy="12"
        r={RING_RADIUS}
        strokeDasharray={RING_LENGTH}
        strokeDashoffset={RING_LENGTH * (1 - progress)}
      />
    </svg>
  );
};

const StateIcon = ({ state, kind, percent }: Pick<Props, "state" | "kind" | "percent">) => {
  switch (state) {
    case "loading":
      return <ProgressRing />;
    case "loading-preview":
      return <ProgressRing percent={percent ?? 0} />;
    case "unloaded-input-error":
      return <img src={arrowClockwiseIcon} alt="" />;
    case "unloaded-error":
      return <img src={arrowFatDownIcon} alt="" />;
    default:
      return kind === "file" ? <img src={fileIcon} alt="" /> : null;
  }
};

export const FileTile = ({
  kind,
  state,
  name,
  previewUrl,
  size,
  percent,
  onClick,
  onDelete,
  onImageLoad,
  onImageError,
}: Props) => {
  const extension = getFileExtension(name);

  return (
    <div
      className={`${styles.tile} ${kind === "img" ? styles.imageTile : styles.fileTile} ${
        onClick ? styles.clickable : ""
      }`}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(event) => {
        if (onClick && (event.key === "Enter" || event.key === " ")) onClick();
      }}
      title={name}
    >
      {kind === "img" ? (
        <>
          {previewUrl ? (
            <img className={styles.image} src={previewUrl} alt={name} onLoad={onImageLoad} onError={onImageError} />
          ) : null}
          {state !== "loaded" ? (
            <div className={styles.overlay}>
              <StateIcon state={state} kind={kind} percent={percent} />
            </div>
          ) : null}
        </>
      ) : (
        <>
          <div className={styles.fileIcon}>
            <StateIcon state={state} kind={kind} percent={percent} />
          </div>
          <div className={styles.fileInfo}>
            <div className={styles.fileName}>{name}</div>
            <div className={styles.fileMeta}>
              {extension ? <span>{extension}</span> : null}
              {extension && size ? <span className={styles.dot} /> : null}
              {size ? <span>{formatMegabytes(size)}MB</span> : null}
            </div>
          </div>
        </>
      )}
      {onDelete ? (
        <button
          type="button"
          className={styles.delete}
          aria-label="Delete"
          onClick={(event) => {
            event.stopPropagation();
            onDelete();
          }}
        >
          <img src={fileDeleteIcon} alt="" />
        </button>
      ) : null}
    </div>
  );
};
